import type { FiscalYearBalance, ForecastPeriodType, ForecastReport } from '@finapp/types';
import { API_BASE_URL, ApiError, fetchWithAuth } from '../config/api';

export class ForecastApiError extends ApiError {
  constructor(message: string, status?: number, details?: unknown) {
    super(message, status, details);
    this.name = 'ForecastApiError';
  }
}

export interface ForecastFilter {
  year: number;
  asOfDate: string; // YYYY-MM-DD
  // Which slice of the year to scope the forecast to. Optional, mirroring the backend's own
  // default - omit (or pass 'ALL') for the original whole-year behavior.
  periodType?: ForecastPeriodType;
}

// Body for PUT /api/v1/fiscal-year-balances/:year. Mirrors the backend's
// UpsertFiscalYearBalanceDto (fiscal-year-balance.controller.ts): opening_balance is
// required, the reconciliation fields are optional.
export interface UpsertFiscalYearBalanceInput {
  opening_balance: number;
  current_balance_override?: number;
  current_balance_note?: string;
}

export const forecastApi = {
  /**
   * Fetch the per-costpoint + org-wide year-end forecast for `year`, as of `asOfDate`.
   * Mirrors reportsApi.ts's convention for building/calling report-style GET endpoints.
   */
  async getForecast(filter: ForecastFilter): Promise<ForecastReport> {
    const queryParams = new URLSearchParams({
      year: filter.year.toString(),
      asOfDate: filter.asOfDate,
    });
    if (filter.periodType) {
      queryParams.set('periodType', filter.periodType);
    }

    const response = await fetchWithAuth(`${API_BASE_URL}/api/v1/forecast?${queryParams}`);

    const data = await response.json();
    return data as ForecastReport;
  },

  /**
   * Create or update the opening balance (and optional reconciliation fields) for a fiscal
   * year. Admin/superadmin only - enforced server-side in fiscal-year-balance.controller.ts;
   * a non-admin calling this directly gets a 403 surfaced as a ForecastApiError.
   */
  async upsertFiscalYearBalance(
    year: number,
    input: UpsertFiscalYearBalanceInput
  ): Promise<FiscalYearBalance> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/v1/fiscal-year-balances/${year}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });

    const data = await response.json();
    return data as FiscalYearBalance;
  },
};
